import { sort } from '@ember/object/computed';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import FundsService from 'donor-elf-web/services/funds-service';
import PowerMenuService from 'donor-elf-web/services/power-menu';
import FundModel from 'donor-elf-web/models/fund';
import RouterService from '@ember/routing/router-service';
import AdminUser from 'donor-elf-web/models/admin/user';

interface modelItem {
  fund: FundModel;
  users: AdminUser[];
}

export default class AdminFundsIndexController extends Controller {
  @service declare fundsService: FundsService;
  @service declare powerMenu: PowerMenuService;
  @service declare router: RouterService;

  queryParams = ['q', 'page', 'showInactive'];
  @tracked q = '';
  @tracked showInactive = false;
  @tracked page = 1;
  perPage = 50;
  @tracked selectedIndex = 0;
  searchInputElement: any;

  declare model: modelItem[];

  get filterApplied() {
    return (this.q || '').length >= 2;
  }

  sortBy = ['fund.name:asc'];
  @sort('model', 'sortBy') declare sortedItems: modelItem[];

  @cached
  get filteredItems() {
    let list = this.sortedItems.slice(0);
    if (!this.showInactive) {
      list = this.sortedItems.filter((record: any) => {
        return record.fund.active;
      });
    }
    if ((this.q || '').length < 2) {
      return list;
    }
    let qLower = this.q.toLowerCase();
    return list.filter((record) => {
      return record.fund.nameLowerCase.indexOf(qLower) >= 0;
    });
  }

  get pagedItems() {
    let offset = (this.page - 1) * this.perPage;
    return this.filteredItems.slice(offset, offset + this.perPage);
  }

  get selectedItem() {
    return this.pagedItems[this.selectedIndex];
  }

  get totalPages() {
    return Math.ceil(this.filteredItems.length / this.perPage);
  }

  @action
  templateDidInsert() {
    this.powerMenu.prependOptionsFn('admin-funds', this.powerMenuOptions);
  }

  @action
  templateWillDestroy() {
    this.powerMenu.removeOptions('admin-funds');
    this.q = '';
    this.page = 1;
    this.selectedIndex = 0;
  }

  @action
  powerMenuOptions() {
    return [
      {
        label: 'Toggle Inactive Funds',
        fn: () => {
          this.showInactive = !this.showInactive;
        },
      },
      {
        label: 'Mark Fund Active',
        fn: async () => {
          let selectedItem = this.selectedItem;
          if (selectedItem == null) {
            alert('A fund is not selected');
            return;
          }
          if (selectedItem.fund.active) {
            alert(
              `The selectd fund (${selectedItem.fund.name}) is already active`,
            );
            return;
          }
          selectedItem.fund.active = true;
          await selectedItem.fund.save();
          this.searchInputElement?.focus();
          this.searchInputElement?.select();
        },
      },
    ];
  }

  @action
  searchInputInserted(el: any) {
    this.searchInputElement = el;
  }

  @action
  toggleShowInactive() {
    this.toggleProperty('showInactive');
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
  }

  @action
  handleSearchInput(e: any) {
    this.q = e.target.value;
    this.page = 1;
    this.selectedIndex = 0;
  }

  @action
  handleSearchEnter(e: any) {
    if (e.target.value !== (this.q || '')) {
      this.handleSearchInput(e);
    }
    let selectedItem = this.selectedItem;
    if (selectedItem) {
      this.router.transitionTo('admin.funds.fund', selectedItem.fund.id);
    }
  }

  @action
  handleSearchKeyDown(e: KeyboardEvent) {
    if (e.which === 40) {
      e.preventDefault();
      if (this.selectedIndex + 1 < this.filteredItems.length) {
        this.selectedIndex += 1;
      }
    } else if (e.which === 38) {
      e.preventDefault();
      if (this.selectedIndex > 0) {
        this.selectedIndex -= 1;
      }
    }
  }

  @action
  viewFund(fundId: string) {
    this.fundsService.viewFund(fundId);
  }

  @action
  async confirmToggleActive(fund: FundModel) {
    let message = `Are you sure you want to mark this fund ${fund.active ? 'Inactive' : 'Active'}?`;
    if (confirm(message)) {
      try {
        fund.active = !fund.active;
        await fund.save();
      } catch (error) {
        alert('There was an error saving the fund');
      }
    }
  }
}
